/**
 * Criterion Industries ESG Page JavaScript
 * Handles animations, interactions, and scroll-based effects
 */

(function() {
    'use strict';

    // ============================================
    // Intersection Observer for Scroll Animations
    // ============================================
    
    const observerOptions = {
        root: null,
        rootMargin: '0px 0px -50px 0px',
        threshold: 0.1
    };

    const animationObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                // Add delay if specified
                const delay = entry.target.dataset.aosDelay || 0;
                
                setTimeout(() => {
                    entry.target.classList.add('aos-animate');
                }, delay);
                
                // Unobserve after animation is triggered
                animationObserver.unobserve(entry.target);
            }
        });
    }, observerOptions);

    // Initialize AOS-like animations
    function initAnimations() {
        const animatedElements = document.querySelectorAll('[data-aos]');
        animatedElements.forEach(el => {
            animationObserver.observe(el);
        });
    }

    // ============================================
    // Counter Animation for Metrics
    // ============================================
    
    function animateCounter(element, target, duration = 2000) {
        const start = 0;
        const increment = target / (duration / 16);
        let current = start;
        
        const timer = setInterval(() => {
            current += increment;
            if (current >= target) {
                current = target;
                clearInterval(timer);
            }
            element.textContent = Math.floor(current) + '%';
        }, 16);
    }

    const counterObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                const target = parseInt(entry.target.dataset.count, 10);
                animateCounter(entry.target, target);
                counterObserver.unobserve(entry.target);
            }
        });
    }, { threshold: 0.5 });

    function initCounters() {
        const counters = document.querySelectorAll('.esg-metric__value[data-count]');
        counters.forEach(counter => {
            counterObserver.observe(counter);
        });
    }

    // ============================================
    // Smooth Scroll for Anchor Links
    // ============================================
    
    function initSmoothScroll() {
        const anchorLinks = document.querySelectorAll('a[href^="#"]');
        
        anchorLinks.forEach(link => {
            link.addEventListener('click', (e) => {
                const href = link.getAttribute('href');
                
                // Skip if it's just "#" or empty
                if (href === '#' || href === '') return;
                
                const target = document.querySelector(href);
                
                if (target) {
                    e.preventDefault();
                    
                    // Calculate offset for fixed header if needed
                    const headerOffset = 80;
                    const elementPosition = target.getBoundingClientRect().top;
                    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

                    window.scrollTo({
                        top: offsetPosition,
                        behavior: 'smooth'
                    });
                }
            });
        });
    }

    // ============================================
    // Pillar Cards Hover Effect Enhancement
    // ============================================
    
    function initPillarCards() {
        const pillarCards = document.querySelectorAll('.esg-pillar-card');
        
        pillarCards.forEach(card => {
            card.addEventListener('mouseenter', () => {
                // Add subtle scale to siblings
                pillarCards.forEach(sibling => {
                    if (sibling !== card) {
                        sibling.style.opacity = '0.7';
                        sibling.style.transform = 'scale(0.98)';
                    }
                });
            });
            
            card.addEventListener('mouseleave', () => {
                // Reset siblings
                pillarCards.forEach(sibling => {
                    sibling.style.opacity = '';
                    sibling.style.transform = '';
                });
            });
        });
    }

    // ============================================
    // Parallax Effect for Hero Section
    // ============================================
    
    function initParallax() {
        const heroImage = document.querySelector('.esg-hero__image');
        
        if (!heroImage) return;
        
        // Reduce motion for users who prefer it
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return;
        }
        
        let ticking = false;
        
        function updateParallax() {
            const scrolled = window.pageYOffset;
            const heroHeight = document.querySelector('.esg-hero').offsetHeight;
            
            if (scrolled <= heroHeight) {
                const parallaxValue = scrolled * 0.3;
                heroImage.style.transform = `scale(1.05) translateY(${parallaxValue}px)`;
            }
            
            ticking = false;
        }
        
        window.addEventListener('scroll', () => {
            if (!ticking) {
                requestAnimationFrame(updateParallax);
                ticking = true;
            }
        }, { passive: true });
    }

    // ============================================
    // Hero Scroll Indicator
    // ============================================
    
    function initScrollIndicator() {
        const scrollIndicator = document.querySelector('.esg-hero__scroll-indicator');
        
        if (!scrollIndicator) return;
        
        // Hide indicator when user scrolls
        let hidden = false;
        
        window.addEventListener('scroll', () => {
            if (!hidden && window.pageYOffset > 100) {
                scrollIndicator.style.opacity = '0';
                scrollIndicator.style.transform = 'translateX(-50%) translateY(20px)';
                hidden = true;
            } else if (hidden && window.pageYOffset <= 100) {
                scrollIndicator.style.opacity = '';
                scrollIndicator.style.transform = '';
                hidden = false;
            }
        }, { passive: true });
        
        // Click to scroll down
        scrollIndicator.addEventListener('click', () => {
            const pillarsSection = document.querySelector('.esg-pillars');
            if (pillarsSection) {
                pillarsSection.scrollIntoView({ behavior: 'smooth' });
            }
        });
        
        scrollIndicator.style.cursor = 'pointer';
    }

    // ============================================
    // Certification Card Image Hover Effect
    // ============================================
    
    function initCertificationCards() {
        const certCards = document.querySelectorAll('.esg-certification-card');
        
        certCards.forEach(card => {
            const image = card.querySelector('.esg-certification-card__image img');
            
            if (!image) return;
            
            card.addEventListener('mouseenter', () => {
                image.style.transform = 'scale(1.05)';
            });
            
            card.addEventListener('mouseleave', () => {
                image.style.transform = '';
            });
        });
    }

    // ============================================
    // Staggered Animation for Grid Items
    // ============================================
    
    function initStaggeredAnimations() {
        const grids = document.querySelectorAll('.esg-governance__grid, .docs-grid');
        
        grids.forEach(grid => {
            const items = grid.children;
            
            Array.from(items).forEach((item, index) => {
                item.style.transitionDelay = `${index * 100}ms`;
            });
        });
    }

    // ============================================
    // Button Ripple Effect
    // ============================================
    
    function initButtonRipple() {
        const buttons = document.querySelectorAll('.btn');
        
        buttons.forEach(button => {
            button.addEventListener('click', function(e) {
                const ripple = document.createElement('span');
                const rect = this.getBoundingClientRect();
                const size = Math.max(rect.width, rect.height);
                const x = e.clientX - rect.left - size / 2;
                const y = e.clientY - rect.top - size / 2;
                
                ripple.style.cssText = `
                    position: absolute;
                    width: ${size}px;
                    height: ${size}px;
                    left: ${x}px;
                    top: ${y}px;
                    background: rgba(255, 255, 255, 0.3);
                    border-radius: 50%;
                    transform: scale(0);
                    animation: ripple 0.6s ease-out;
                    pointer-events: none;
                `;
                
                this.style.position = 'relative';
                this.style.overflow = 'hidden';
                this.appendChild(ripple);
                
                setTimeout(() => ripple.remove(), 600);
            });
        });
        
        // Add ripple keyframes if not already present
        if (!document.querySelector('#ripple-styles')) {
            const style = document.createElement('style');
            style.id = 'ripple-styles';
            style.textContent = `
                @keyframes ripple {
                    to {
                        transform: scale(4);
                        opacity: 0;
                    }
                }
            `;
            document.head.appendChild(style);
        }
    }

    // ============================================
    // Mobile Menu / Viewport Detection
    // ============================================
    
    function initViewportDetection() {
        // Add class to body based on viewport
        function updateViewportClass() {
            const width = window.innerWidth;
            document.body.classList.remove('is-mobile', 'is-tablet', 'is-desktop');
            
            if (width < 600) {
                document.body.classList.add('is-mobile');
            } else if (width < 900) {
                document.body.classList.add('is-tablet');
            } else {
                document.body.classList.add('is-desktop');
            }
        }
        
        updateViewportClass();
        window.addEventListener('resize', debounce(updateViewportClass, 100));
    }

    // ============================================
    // Utility: Debounce Function
    // ============================================
    
    function debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    }

    // ============================================
    // Lazy Load Images
    // ============================================
    
    function initLazyLoad() {
        if ('loading' in HTMLImageElement.prototype) {
            // Native lazy loading supported
            const images = document.querySelectorAll('img[data-src]');
            images.forEach(img => {
                img.src = img.dataset.src;
            });
        } else {
            // Fallback for older browsers
            const imageObserver = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const img = entry.target;
                        if (img.dataset.src) {
                            img.src = img.dataset.src;
                            img.removeAttribute('data-src');
                        }
                        imageObserver.unobserve(img);
                    }
                });
            });

            const lazyImages = document.querySelectorAll('img[data-src]');
            lazyImages.forEach(img => imageObserver.observe(img));
        }
    }

    // ============================================
    // Accessibility: Reduced Motion
    // ============================================
    
    function checkReducedMotion() {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            document.body.classList.add('reduced-motion');
            
            // Disable animations
            const style = document.createElement('style');
            style.textContent = `
                .reduced-motion * {
                    animation-duration: 0.01ms !important;
                    animation-iteration-count: 1 !important;
                    transition-duration: 0.01ms !important;
                }
            `;
            document.head.appendChild(style);
        }
    }

    // ============================================
    // Initialize Everything on DOM Ready
    // ============================================
    
    function init() {
        checkReducedMotion();
        initAnimations();
        initCounters();
        initSmoothScroll();
        initPillarCards();
        initParallax();
        initScrollIndicator();
        initCertificationCards();
        initStaggeredAnimations();
        initButtonRipple();
        initViewportDetection();
        initLazyLoad();
        
        // Add loaded class for any CSS hooks
        document.body.classList.add('esg-loaded');
    }

    // Run on DOM ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

})();